import { isProduction } from "@/config/index.js";

const getKeyLocalorTest = (shopkey: string) => {
  if (isProduction) return;
  return localStorage.getItem("b2c_override") === shopkey;
};

const importInterFont = () => {
  import("@fontsource/inter/200.css");
  import("@fontsource/inter/300.css");
  import("@fontsource/inter/400.css");
  import("@fontsource/inter/500.css");
  import("@fontsource/inter/600.css");
  import("@fontsource/inter/700.css");
};

const importRobotoFont = () => {
  import("@fontsource/roboto-condensed");
  import("@fontsource/roboto-condensed/700.css");
  import("@fontsource/roboto-condensed");
};

const importBarlowFont = () => {
  import("@fontsource/barlow");
  import("@fontsource/barlow/400.css");
  import("@fontsource/barlow-condensed"); // Defaults to weight 400
  import("@fontsource/barlow-condensed/400.css");
  import("@fontsource/barlow-condensed/600.css"); // Specify weight
  import("@fontsource/barlow-condensed/400-italic.css"); // Specify weight and style
};

// const importMontserrat = () => {
//   import("@fontsource/montserrat");
//   import("@fontsource/montserrat/800.css");
//   import("@fontsource/montserrat/900.css"); // Specify weight
//   import("@fontsource/montserrat/400-italic.css"); // Specify weight and style
// };

const importPoppinsFont = () => {
  import("@fontsource/poppins");
  import("@fontsource/poppins/200.css");
  import("@fontsource/poppins/300.css");
  import("@fontsource/poppins/400.css");
  import("@fontsource/poppins/500.css");
  import("@fontsource/poppins/600.css");
  import("@fontsource/poppins/700.css");
  import("@fontsource/poppins/400-italic.css"); // Specify weight and style
};

const importPriorityStyling = () => {
  import("@/assets/styles/brand/priority.css");
  importInterFont();
};

const importXs2EventStyling = () => {
  import("@/assets/styles/brand/xs2event.css");
  importInterFont();
};

export const b2shops: { text: string; key: string; importStyling: () => void; theme?: string }[] = [
  {
    text: "Priority",
    key: "priorityshopf1",
    theme: "priority",
    importStyling: () => importPriorityStyling(),
  },
  {
    text: "Priority MotoGP",
    key: "priorityshopmotogp",
    theme: "priority",
    importStyling: () => importPriorityStyling(),
  },
  {
    text: "Priority Football",
    key: "priority",
    theme: "priority",
    importStyling: () => importPriorityStyling(),
  },
  {
    text: "Voetbalzone",
    key: "voetbalzone",
    importStyling: () => {
      import("@/assets/styles/brand/voetbalzone.css");
      importInterFont();
    },
  },
  {
    text: "GPFans",
    key: "gpfans",
    importStyling: () => {
      import("@/assets/styles/brand/gpfans.css");
      importRobotoFont();
    },
  },
  {
    text: "GP Destinations",
    key: "gpdestinations",
    importStyling: () => {
      import("@/assets/styles/brand/gpdestinations.css");
      importRobotoFont();
    },
  },
  {
    text: "FC Update",
    key: "fcupdate",
    importStyling: () => {
      import("@/assets/styles/brand/fcupdate.css");
      importInterFont();
    },
  },
  {
    text: "Motor.nl",
    key: "motor",
    importStyling: () => {
      import("@/assets/styles/brand/motor.css");
      importBarlowFont();
    },
  },
  {
    text: "Helden",
    key: "helden.media",
    theme: "helden",
    importStyling: () => {
      import("@/assets/styles/brand/helden.css");
      importBarlowFont();
    },
  },
  {
    text: "Racesport",
    key: "racesport.nl",
    theme: "racesport",
    importStyling: () => {
      import("@/assets/styles/brand/racesport.css");
      importBarlowFont();
    },
  },
  {
    text: "Homefans",
    key: "homefans",
    importStyling: () => {
      import("@/assets/styles/brand/homefans.css");
      importPoppinsFont();
    },
  },
  {
    text: "Sporttravel",
    key: "sporttravel",
    importStyling: () => {
      import("@/assets/styles/brand/sporttravel.css");
      importPoppinsFont();
    },
  },
  {
    text: "XS2Event",
    key: "xs2event",
    importStyling: () => importXs2EventStyling(),
  },
] as const;

export type Brand = (typeof b2shops)[number]["key"];

const brandedShops = new Set<(typeof b2shops)[number]["key"]>([
  "priorityshopf1",
  "priorityshopmotogp",
  //Priority should be checked last, as it's the least strict check
  "priority",
  "voetbalzone",
  "gpfans",
  "gpdestinations",
  "fcupdate",
  "motor",
  "racesport.nl",
  "helden.media",
  "homefans",
  "sporttravel",
]);

export const shop =
  b2shops.find((x) => {
    const key = x.key;
    if (!key) return;
    return window.location.hostname.indexOf(key) > -1 || getKeyLocalorTest(key);
  }) || b2shops[b2shops.length - 1];

shop.importStyling();

document.body.className = "theme-" + (shop.theme || shop.key);

export const isPriorityf1 = shop.key == "priorityshopf1";
export const isPriority = shop.key == "priority";
export const isVoetbalzone = shop.key == "voetbalzone";
export const isGPFans = shop.key == "gpfans";
export const isGPDestinations = shop.key == "gpdestinations";
export const isFCUpdate = shop.key == "fcupdate";
export const isMotor = shop.key == "motor";
export const isheldenmedia = shop.key == "helden.media";
export const isPrioritymotogp = shop.key == "priorityshopmotogp";
export const isRacesport = shop.key == "racesport.nl";
export const isHomefans = shop.key == "homefans";
export const isSportTravel = shop.key == "sporttravel";

export const isB2C = shop.key ? brandedShops.has(shop.key) : false;

export const isPriorityShop =
  shop.key == "priority" || shop.key == "priorityshopmotogp" || shop.key == "priorityshopf1";

export const isXS2EventShop = shop.key == "xs2event";

type BrandConfig = ReturnType<() => typeof import("@/config/branding/xs2event")>["default"];

export const brandconfig: BrandConfig = {};

export const ready = (async () => {
  const loadedBrandConfig = (await import(`@/config/branding/${shop.key}.js`)).default as BrandConfig;
  Object.assign(brandconfig, loadedBrandConfig); // Update the object by reference
  return brandconfig;
})();

export default brandconfig;
